import React, { useState, useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FolderOpen,
  Plus,
  FileSpreadsheet,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Home,
  Settings,
  MessageSquare,
  Globe,
  Check,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Logo } from "./Logo";
import { useAuth } from "@/features/auth";
import { useToast } from "@/shared/components/ui/use-toast";
import { useProjectsQuery } from "@/features/project/hooks/useProjectQuery";
import { cn } from "@/shared/lib/utils";
import { DreAnalysisDialog } from "@/features/dre";
import { useIsAdmin } from "@/features/admin/hooks/useIsAdmin";
import { useCreateSession, useSessionsQuery } from "@/features/session/hooks/useSessionQuery";
import { getPrrProjectId, getSessionRoute, isPrrMode } from "@/prr";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/shared/lib/i18n";
import { isFeatureEnabled } from "@/shared/lib/featureFlags";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";

interface SidebarProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function Sidebar({ collapsed = false, onCollapsedChange }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { toast } = useToast();
  const prrMode = isPrrMode();
  const { data: projects = [], isLoading: loadingProjects } = useProjectsQuery(!prrMode);
  const { data: sessions = [], isLoading: loadingSessions } = useSessionsQuery(undefined);
  const createSessionMutation = useCreateSession();
  const [openDre, setOpenDre] = useState(false);
  const isAdmin = useIsAdmin();
  const userProjectId = getPrrProjectId(user);
  const { t, i18n } = useTranslation("sidebar");
  const showDreAnalysis = isFeatureEnabled("DRE_ANALYSIS");

  // Match projectId from /project/:id - captures any ID format (numeric or UUID)
  const currentProjectId = useMemo(() =>
    location.pathname.match(/\/project\/([^/]+)/)?.[1],
    [location.pathname]
  );
  const currentSessionId = useMemo(() =>
    location.pathname.match(/\/session\/([^/]+)/)?.[1],
    [location.pathname]
  );

  const handleLogout = useCallback(() => {
    logout();
    toast({
      title: t("logoutSuccess"),
      description: t("logoutDescription"),
    });
  }, [logout, toast, t]);

  const handlePrimaryCreate = useCallback(async () => {
    if (!prrMode) {
      navigate("/dashboard/new-project");
      return;
    }

    try {
      const newSession = await createSessionMutation.mutateAsync({
        projectId: userProjectId,
        name: "Nova Sessao",
      });
      navigate(getSessionRoute(newSession.id, newSession.project_id));
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao criar sessao",
        description: "Nao foi possivel criar uma nova sessao.",
      });
    }
  }, [prrMode, navigate, createSessionMutation, userProjectId, toast]);

  const userInitials = useMemo(() => {
    return user?.given_name
      ? user.given_name.charAt(0).toUpperCase()
      : user?.name?.charAt(0).toUpperCase() || "U";
  }, [user]);

  const userName = useMemo(() => {
    return user?.given_name || user?.name || user?.preferred_username || t("user");
  }, [user, t]);

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          "h-full bg-visagio-teal-dark flex flex-col transition-all duration-300 shrink-0 shadow-visagio-lg",
          collapsed ? "w-16" : "w-64"
        )}
      >
        {/* Header */}
        <div className={cn(
          "h-16 border-b border-visagio-teal/30 flex items-center",
          collapsed ? "justify-center px-2" : "justify-between px-4"
        )}>
          <Link to="/dashboard" className="flex items-center">
            <Logo size={collapsed ? "sm" : "lg"} showText={!collapsed} />
          </Link>
          {!collapsed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-white/70 hover:text-white hover:bg-visagio-teal/50"
                  onClick={() => onCollapsedChange?.(!collapsed)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-visagio-teal text-white border-none">
                {t("collapse")}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Expand button when collapsed */}
        {collapsed && (
          <div className="px-2 py-3 border-b border-visagio-teal/30">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="w-full text-white/70 hover:text-white hover:bg-visagio-teal/50"
                  onClick={() => onCollapsedChange?.(!collapsed)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-visagio-teal text-white border-none">
                {t("expand")}
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Navigation - flex column to position sections */}
        <nav className="flex-1 flex flex-col py-4 min-h-0">
          {/* Main Actions - FIXED at top */}
          <div className={cn("px-3 space-y-1 shrink-0", collapsed && "px-2")}>
            <SidebarItem
              icon={Home}
              label={t("dashboard")}
              href="/dashboard"
              active={location.pathname === "/dashboard" || location.pathname.startsWith("/dashboard/session/")}
              collapsed={collapsed}
            />
          </div>

          {/* Projects Section - SCROLLABLE, takes remaining space */}
          <div className="mt-6 flex-1 min-h-0 flex flex-col">
            {!collapsed && (
              <div className="px-4 mb-3 flex items-center justify-between">
                <span className="text-xs font-heading font-semibold text-white/60 uppercase tracking-widest">
                  {prrMode ? "Sessões" : t("projects")}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="h-6 w-6 text-white/60 hover:text-white hover:bg-visagio-teal/50"
                      onClick={handlePrimaryCreate}
                      disabled={createSessionMutation.isPending}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-visagio-teal text-white border-none">
                    {prrMode ? "Nova Sessão" : t("newProject")}
                  </TooltipContent>
                </Tooltip>
              </div>
            )}

            <div
              className={cn(
                "px-3 space-y-1 flex-1 min-h-0 overflow-y-auto scrollbar-elegant",
                collapsed && "px-2"
              )}
            >
              {collapsed && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="w-full text-white/60 hover:text-white hover:bg-visagio-teal/50"
                      onClick={handlePrimaryCreate}
                      disabled={createSessionMutation.isPending}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-visagio-teal text-white border-none">
                    {prrMode ? "Nova Sessão" : t("newProject")}
                  </TooltipContent>
                </Tooltip>
              )}

              {prrMode ? (
                loadingSessions ? (
                  <div className={cn("py-4 text-center", collapsed && "py-2")}>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                  </div>
                ) : sessions.length > 0 ? (
                  sessions.map((session) => (
                    <SidebarItem
                      key={session.id}
                      icon={MessageSquare}
                      label={session.name || `Sessão ${session.id.slice(0, 8)}`}
                      href={getSessionRoute(session.id, session.project_id)}
                      active={currentSessionId === session.id}
                      collapsed={collapsed}
                    />
                  ))
                ) : (
                  !collapsed && (
                    <p className="text-xs text-white/40 px-2 py-2 font-body">
                      Nenhuma sessao ainda
                    </p>
                  )
                )
              ) : loadingProjects ? (
                <div className={cn("py-4 text-center", collapsed && "py-2")}>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : projects.length > 0 ? (
                projects.map((project) => (
                  <SidebarItem
                    key={project.id}
                    icon={FolderOpen}
                    label={project.name}
                    href={`/dashboard/project/${project.id}`}
                    active={currentProjectId === project.id}
                    collapsed={collapsed}
                  />
                ))
              ) : (
                !collapsed && (
                  <p className="text-xs text-white/40 px-2 py-2 font-body">
                    {t("noProjectsYet")}
                  </p>
                )
              )
              }
            </div>
          </div>

          {/* Tools Section - FIXED at bottom of nav (Feature flagged) */}
          {showDreAnalysis && (
            <div className="mt-6 shrink-0">
              {!collapsed && (
                <div className="px-4 mb-3">
                  <span className="text-xs font-heading font-semibold text-white/60 uppercase tracking-widest">
                    {t("tools")}
                  </span>
                </div>
              )}
              <div className={cn("px-3 space-y-1", collapsed && "px-2")}>
                <SidebarItem
                  icon={FileSpreadsheet}
                  label={t("dreAnalysis")}
                  onClick={() => setOpenDre(true)}
                  collapsed={collapsed}
                />
              </div>
            </div>
          )}
        </nav>

        {/* User Section */}
        <div className="border-t border-visagio-teal/30 p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start gap-3 h-12 text-white hover:bg-visagio-teal/50",
                  collapsed && "justify-center px-0"
                )}
              >
                <div className="h-8 w-8 rounded-full bg-visagio-gradient flex items-center justify-center text-sm font-heading font-bold text-white shrink-0 shadow-sm">
                  {userInitials}
                </div>
                {!collapsed && (
                  <span className="truncate font-body text-sm">{userName}</span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align={collapsed ? "center" : "start"}
              side="top"
              className="w-56 bg-white border-visagio-off-white shadow-visagio-lg rounded-xl"
            >
              <DropdownMenuLabel className="font-body">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-heading font-semibold text-visagio-teal-dark">{userName}</p>
                  {user?.email && (
                    <p className="text-xs text-visagio-gray">{user.email}</p>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-visagio-off-white" />
              {isAdmin && (
                <DropdownMenuItem
                  onClick={() => navigate("/admin")}
                  className="text-visagio-teal cursor-pointer font-body hover:bg-visagio-off-white rounded-lg mx-1"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  {t("adminPage")}
                </DropdownMenuItem>
              )}
              {isAdmin && <DropdownMenuSeparator className="bg-visagio-off-white" />}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="text-visagio-teal cursor-pointer font-body hover:bg-visagio-off-white rounded-lg mx-1">
                  <Globe className="h-4 w-4 mr-2" />
                  {t("language")}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="bg-white border-visagio-off-white shadow-visagio-lg rounded-xl min-w-[140px]">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => i18n.changeLanguage(lang.code)}
                      className="cursor-pointer font-body hover:bg-visagio-off-white rounded-lg mx-1 flex items-center justify-between"
                    >
                      {lang.label}
                      {i18n.language === lang.code && (
                        <Check className="h-4 w-4 text-visagio-teal ml-2" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator className="bg-visagio-off-white" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 cursor-pointer font-body hover:bg-red-50 rounded-lg mx-1"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t("logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {showDreAnalysis && <DreAnalysisDialog open={openDre} onOpenChange={setOpenDre} />}
      </aside>
    </TooltipProvider>
  );
}

// Sidebar Item Component - Memoized to prevent unnecessary re-renders
interface SidebarItemProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href?: string;
  onClick?: () => void;
  active?: boolean;
  collapsed?: boolean;
}

const SidebarItem = React.memo(function SidebarItem({ icon: Icon, label, href, onClick, active, collapsed }: SidebarItemProps) {
  const content = (
    <div
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-xl font-body text-sm font-medium transition-all duration-200 cursor-pointer group",
        active
          ? "bg-visagio-teal text-white shadow-md"
          : "text-white/70 hover:text-white hover:bg-visagio-teal/50",
        collapsed && "justify-center px-2"
      )}
    >
      <Icon className={cn(
        "h-4 w-4 shrink-0 transition-transform duration-200",
        collapsed && "h-5 w-5",
        !active && "group-hover:scale-110"
      )} />
      {!collapsed && <span className="truncate">{label}</span>}
    </div>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {href ? (
            <Link to={href}>{content}</Link>
          ) : (
            <div onClick={onClick}>{content}</div>
          )}
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-visagio-teal text-white border-none font-body">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (href) {
    return <Link to={href}>{content}</Link>;
  }

  return <div onClick={onClick}>{content}</div>;
});
